/**
 * SpecialtyPharmacyCoverageSection - Output section for specialty pharmacy coverage
 *
 * Displays specialty pharmacy coverage information as a top-level accordion section.
 */

import { AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import React from 'react';

import { FieldPathProvider } from '../context';
import { Field, SectionTriggerScore } from './Field';

interface SpecialtyPharmacyCoverageSectionProps {
  data?: Record<string, unknown>;
  sectionId: string;
}

export const SpecialtyPharmacyCoverageSection: React.FC<SpecialtyPharmacyCoverageSectionProps> = ({
  data,
  sectionId,
}) => {
  return (
    <AccordionItem value={sectionId} className="border-b">
      <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:bg-muted/50">
        <div className="flex items-center gap-2 flex-1">
          <span>Drug Specialty Pharmacy Coverage</span>
          <SectionTriggerScore subsectionKey="specialty_pharmacy_coverage" />
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <FieldPathProvider prefix="drug.specialty_pharmacy_coverage">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <Field
              label="Coverage Status"
              value={data?.specialty_pharmacy_coverage_status}
              fieldId="specialty_pharmacy_coverage_status"
            />
            <Field
              label="SP Coverage"
              value={data?.specialty_pharmacy_coverage}
              fieldId="specialty_pharmacy_coverage"
            />
            <Field
              label="Coinsurance"
              value={data?.specialty_pharmacy_coinsurance}
              fieldId="specialty_pharmacy_coinsurance"
            />
            <Field
              label="Copay"
              value={data?.specialty_pharmacy_copay}
              fieldId="specialty_pharmacy_copay"
            />
            <Field
              label="Covered 100%"
              value={data?.specialty_pharmacy_covered_hundred_percent}
              fieldId="specialty_pharmacy_covered_hundred_percent"
            />
            <Field
              label="Deductible Applies"
              value={data?.specialty_pharmacy_deductible_applies}
              fieldId="specialty_pharmacy_deductible_applies"
            />
            <Field
              label="OOP Max Applies"
              value={data?.specialty_pharmacy_oopmax_applies}
              fieldId="specialty_pharmacy_oopmax_applies"
            />
            {/* See CoverageSection: the "applies" flags separate "no copay" from an
                unextracted copay, both of which render as a blank amount (LA-5202). */}
            <Field
              label="Copay Applies"
              value={data?.specialty_pharmacy_copay_applies}
              fieldId="specialty_pharmacy_copay_applies"
            />
            <Field
              label="Coinsurance Applies"
              value={data?.specialty_pharmacy_coinsurance_applies}
              fieldId="specialty_pharmacy_coinsurance_applies"
            />
            <Field
              label="Same Coverage"
              value={data?.specialty_pharmacy_same_coverage}
              fieldId="specialty_pharmacy_same_coverage"
            />
            {data?.specialty_pharmacy_reason_for_noncoverage != null && (
              <div className="col-span-2">
                <Field
                  label="Reason for Non-Coverage"
                  value={data.specialty_pharmacy_reason_for_noncoverage}
                  fieldId="specialty_pharmacy_reason_for_noncoverage"
                />
              </div>
            )}
          </div>
        </FieldPathProvider>
      </AccordionContent>
    </AccordionItem>
  );
};
